import * as React from "react";
import { ProductTemplateId, ProductPhase } from "@modules/tasks/types";
import { Typography } from "@earned/core";
import { WealthPlaybookDomainMiniDisplay } from "../WealthPlaybookDomainMiniDisplay";
import { WealthPlaybookDomainDisplay } from "../WealthPlaybookDomainDisplay";
import { useBreakpoints } from "@common/hooks";
import { PLAYBOOK_DOMAINS } from "@modules/tasks/constants";
import { useProgressByPlaybookDomain } from "@modules/tasks/hooks";
import cx from "classnames";

interface PizzaTrackerContainerProps {
  product: Forme.Product["templateId"];
  currentPhase: ProductPhase;
  onClick: (phase: ProductPhase) => void;
  openTasks: Forme.Task[];
  completedTasks: Forme.Task[];
  ongoingWealthManagement: boolean | null;
}

const PizzaTrackerContainer: React.FC<PizzaTrackerContainerProps> = ({
  product,
  openTasks,
  completedTasks,
  ongoingWealthManagement,
}) => {
  const progressByPlaybookDomain = useProgressByPlaybookDomain(
    openTasks,
    completedTasks
  );
  const { isMobile, isDesktop } = useBreakpoints();
  const playbookStatus = React.useMemo(() => {
    if (
      ongoingWealthManagement ||
      product === ProductTemplateId.WEALTH_DIAGNOSTIC
    )
      return "Optimizing your financial life";
    return "Building and implementing your Doctor Wealth Playbook";
  }, [ongoingWealthManagement, product]);

  const playbookDomains = React.useMemo(
    () =>
      ongoingWealthManagement
        ? [
            ...PLAYBOOK_DOMAINS.filter(
              (domain) => domain !== ProductPhase.PHASE_1
            ),
          ].reverse()
        : [...PLAYBOOK_DOMAINS].reverse(),
    [ongoingWealthManagement]
  );

  return (
    <div className="sticky top-0 z-10 flex w-full select-none flex-col gap-5 bg-green-3 px-6 py-8 pt-6 md:gap-[17px] md:bg-green-4 md:px-10 md:py-7">
      <div className="flex w-full flex-row items-center justify-center md:justify-start">
        {product ? (
          <Typography
            variant={!isDesktop ? "title-sm" : "title-lg"}
            color="light"
            weight="medium"
          >
            Your Wealth{" "}
            {product === ProductTemplateId.WEALTH_DIAGNOSTIC
              ? "Diagnostic"
              : "Playbook"}
          </Typography>
        ) : (
          <Typography
            variant={!isDesktop ? "title-sm" : "title-lg"}
            color="light"
            weight="medium"
          >
            Tasks & Actions
          </Typography>
        )}
      </div>
      {product && (
        <>
          {isMobile ? (
            <div className="flex flex-row justify-center">
              {playbookDomains.map((playbookDomain, index) => (
                <div
                  className={cx(!ongoingWealthManagement && "-ml-[8px]")}
                  style={{ zIndex: 10 - index }}
                  key={playbookDomain}
                >
                  <WealthPlaybookDomainMiniDisplay
                    playbookDomain={playbookDomain}
                    progress={
                      ongoingWealthManagement
                        ? 100
                        : progressByPlaybookDomain[playbookDomain]
                    }
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <Typography variant="body" color="light">
                {playbookStatus}
              </Typography>
              <div className="flex flex-row gap-2">
                {playbookDomains.map((playbookDomain) => (
                  <div key={playbookDomain}>
                    <WealthPlaybookDomainDisplay
                      playbookDomain={playbookDomain}
                      progress={
                        ongoingWealthManagement
                          ? 100
                          : progressByPlaybookDomain[playbookDomain]
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PizzaTrackerContainer;
